import React, { useState, useRef, useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { HeroIcons } from '@/components/design-system/atoms/icon';
import { useSearchRoomContext } from '@/features/marketplace/hooks/use-search-room';
import { theme } from '@/utils/util_theme';
import { SearchBarItems, GotoSearchPageButton } from '../search-bar-items';
import { SearchBarProps } from './types';
import clsx from 'clsx';

const SearchBarHeader: React.FC<SearchBarProps> = ({
  className,
  autoFocus,
  onClose,
  onFocusChange,
  showGotoSearchPageButton = true,
  showDropdownOnEmptyFocus = true,
}) => {
  const [inputActive, setInputActive] = useState(false);
  const { searchQuery, setSearchQuery } = useSearchRoomContext();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const reset = () => {
    setSearchQuery(undefined);
  };

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      setInputActive(true);
      onFocusChange?.(true);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!onClose) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setInputActive(false);
      onFocusChange?.(false);
    }, 200);
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        'searchbar',
        inputActive && 'searchbar-active',
        onClose && 'min-w-[150px] max-w-[400px] flex-shrink-[3]',
        className,
        'mr-2'
      )}
    >
      <div className="searchbar-input-wrapper">
        <HeroIcons
          name="magnifying-glass"
          type="outline/medium"
          strokeColor={theme?.colors.fill.icon.primaryMedium}
        />

        <input
          ref={inputRef}
          value={searchQuery || ''}
          className="searchbar-input"
          type="text"
          placeholder={t('js.features.marketplace.components.room_search.placeholder')}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => { setInputActive(true); onFocusChange?.(true); }}
          onBlur={handleBlur}
        />

        {searchQuery && searchQuery !== '' && (
          <HeroIcons
            name="x-mark"
            type="outline/medium"
            strokeColor={theme?.colors.fill.icon.primaryLight}
            className="cursor-pointer"
            onClick={() => reset()}
          />
        )}

        {onClose && (
          <HeroIcons
            name="x-mark"
            type="outline/medium"
            strokeColor={theme?.colors.fill.icon.primaryHeavy}
            className="ml-2 cursor-pointer"
            onClick={onClose}
          />
        )}
      </div>

      <Transition
        show={showDropdownOnEmptyFocus ? inputActive : (inputActive && !!searchQuery)}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
        className="relative z-20"
      >
        <div className="searchbar-items-wrapper">
          <ReactSVG
            src={require(`@assets/icons/searchbar/pointer.svg`)}
            className="searchbar-items-pointer"
          />

          <div className="searchbar-items searchbar-items-default">
            <div className="searchbar-items-content">
              <SearchBarItems showGotoSearchPageButton={false} />
            </div>
            {showGotoSearchPageButton && (
              <div className="searchbar-items-sticky-button">
                <GotoSearchPageButton />
              </div>
            )}
          </div>
        </div>
      </Transition>
    </div>
  );
};

export { SearchBarHeader };
